import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, CardBody, FormField, Icon, Input, errorMessage } from "@crc/ui";
import { api } from "../lib/api.ts";
import { useAuth, homeFor } from "../lib/auth.tsx";
import { ThemeSwitchInline } from "./shared/ThemeSwitchInline.tsx";

function AuthFrame({ children, title, subtitle }: { children: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="brand-block">
          <img src="/crc-logo.png" alt="CRC Kashrus" style={{ height: 40 }} />
          <div>
            <h1>{title}</h1>
            {subtitle && <div className="muted small">{subtitle}</div>}
          </div>
        </div>
        <Card>
          <CardBody>{children}</CardBody>
        </Card>
        <div className="mt-4 flex" style={{ justifyContent: "center" }}>
          <ThemeSwitchInline />
        </div>
      </div>
    </div>
  );
}

export function SignIn() {
  const [params] = useSearchParams();
  const next = params.get("next") ?? "";
  const error = params.get("error");
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { data: providers } = useQuery({ queryKey: ["sso-providers"], queryFn: () => api.get<{ mocked: boolean; providers: Array<{ key: string }> }>("/auth/sso/providers") });

  useEffect(() => {
    if (me) navigate(next || homeFor(me), { replace: true });
  }, [me, next, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.post("/auth/login", { email, password });
      await refresh();
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };
  const sso = (provider: string) => {
    if (providers?.mocked) navigate(`/sign-in/mock?provider=${provider}&next=${encodeURIComponent(next)}`);
    else window.location.href = `/api/v1/auth/sso/${provider}/start`;
  };

  return (
    <AuthFrame title="Sign in" subtitle="CRC Kashrus certification platform">
      {error && <Alert tone="danger" className="mb-4">{describeSsoError(error)}</Alert>}
      <div className="small muted mb-3" style={{ fontWeight: 600 }}>CRC staff</div>
      <Button className="provider-btn" onClick={() => sso("microsoft")} icon="globe">
        Continue with Microsoft
      </Button>
      <Button className="provider-btn" onClick={() => sso("google")} icon="globe">
        Continue with Google
      </Button>
      {providers?.mocked && <div className="small muted">Local mode: providers are mocked — you will pick a seeded staff account.</div>}
      <div className="divider">companies &amp; mashgichim</div>
      <form onSubmit={submit}>
        <FormField label="Email" htmlFor="email">
          <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </FormField>
        <FormField label="Password" htmlFor="password">
          <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </FormField>
        {err && <Alert tone="danger" className="mb-4">{err}</Alert>}
        <Button type="submit" variant="primary" loading={busy} style={{ width: "100%" }}>
          Sign in
        </Button>
      </form>
      <p className="small muted mt-4">Accounts are created by invitation only. Not certified yet? <a href="/apply">Apply for certification</a>.</p>
    </AuthFrame>
  );
}

function describeSsoError(code: string) {
  switch (code) {
    case "no_invite":
      return "There is no invitation for that email address. Ask CRC to invite you, then sign in with the same email.";
    case "email_not_verified":
      return "Your provider did not confirm the email address. Verify it with Microsoft or Google and try again.";
    case "deactivated":
      return "This account has been deactivated.";
    default:
      return `Sign-in failed (${code}).`;
  }
}

/** Local mode only: pick a seeded staff account and simulate the provider callback. */
export function MockSsoChooser() {
  const [params] = useSearchParams();
  const provider = (params.get("provider") ?? "microsoft") as "microsoft" | "google";
  const next = params.get("next") ?? "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [custom, setCustom] = useState("");
  const [verified, setVerified] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ["mock-accounts"], queryFn: () => api.get<{ accounts: Array<{ email: string; name: string; userType: string; roleKey: string }> }>("/auth/sso/mock/accounts") });
  const go = async (email: string) => {
    setErr(null);
    try {
      await api.post("/auth/sso/mock", { provider, subject: `${provider}-${email}`, email, emailVerified: verified });
      await refresh();
      navigate(next || "/", { replace: true });
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  const staff = (data?.accounts ?? []).filter((a) => a.userType === "internal");
  return (
    <AuthFrame title={`Mock ${provider === "microsoft" ? "Microsoft" : "Google"} sign-in`} subtitle="Local development only — no cloud account needed">
      {err && <Alert tone="danger" className="mb-4">{err}</Alert>}
      <div className="small muted mb-3">The same gate as production applies: the email must belong to an invited, active user and be verified.</div>
      <div className="stack">
        {staff.map((a) => (
          <Button key={a.email} className="provider-btn" onClick={() => go(a.email)} icon="user">
            <span style={{ flex: 1, textAlign: "left" }}>
              {a.name} <span className="muted small">· {a.roleKey.replace(/_/g, " ")} · {a.email}</span>
            </span>
            <Icon name="arrow" size={14} />
          </Button>
        ))}
      </div>
      <div className="divider">or any email</div>
      <FormField label="Email the provider would assert" htmlFor="custom">
        <Input id="custom" type="email" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="someone@anydomain.example" />
      </FormField>
      <label className="check-row">
        <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} /> Provider reports email_verified
      </label>
      <Button variant="primary" disabled={!custom} onClick={() => go(custom)} style={{ width: "100%" }}>
        Continue as {custom || "…"}
      </Button>
      <p className="small muted mt-4"><a href="/sign-in">Back to sign in</a></p>
    </AuthFrame>
  );
}
